import { Button, Card, CardContent, CardHeader, CardTitle } from '@design-system';
import { StripeEmbeddedCheckout } from '@/features/advisor-onboarding';
import { WagDisclosure } from './wag-disclosure';

import { Check, ChevronLeft, Lock } from 'lucide-react';

import type { FreemiumTierConfig } from '@shared';

interface CheckoutStepProps {
  config: FreemiumTierConfig;
  clientSecret: string;
  onBack: () => void;
}

export function CheckoutStep({ config, clientSecret, onBack }: CheckoutStepProps) {
  const isSubscription = config.priceModel === 'subscription';
  const chargeLabel = isSubscription
    ? `You'll be billed $${config.priceUsd}/month until you cancel.`
    : `You'll be charged $${config.priceUsd} one time.`;

  return (
    <div className="mx-auto w-full max-w-6xl">
      <div className="grid items-start gap-6 lg:grid-cols-[5fr_6fr] lg:gap-12">
        <div>
          <Button
            size="sm"
            variant="outline"
            onClick={onBack}
          >
            <ChevronLeft />
            Back to options
          </Button>

          <Card className="bg-primary border-primary mt-6 shadow-none">
            <CardHeader>
              <CardTitle className="text-primary-foreground">{config.name}</CardTitle>
              <div className="mt-2 flex items-baseline gap-1.5">
                <span className="text-primary-foreground text-4xl font-bold tracking-tight">
                  ${config.priceUsd}
                </span>
                <span className="text-primary-foreground/70 text-sm">
                  {config.priceSuffix ?? 'one-time'}
                </span>
              </div>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2.5">
                {config.features.map(feature => (
                  <li
                    key={feature}
                    className="flex items-start gap-2 text-sm"
                  >
                    <Check className="text-primary-foreground mt-0.5 size-4 shrink-0" />
                    <span className="text-primary-foreground/90">{feature}</span>
                  </li>
                ))}
              </ul>
              {config.isLicensed && (
                <WagDisclosure className="text-primary-foreground/70 mt-6 text-xs leading-relaxed" />
              )}
            </CardContent>
          </Card>
        </div>

        <Card className="overflow-hidden p-0 shadow-none">
          <div className="border-b px-5 py-4">
            <div className="flex items-center gap-2">
              <Lock className="text-foreground size-4" />
              <span className="font-semibold text-gray-950">Secure checkout</span>
            </div>
            <p className="text-foreground mt-1 text-sm">{chargeLabel}</p>
          </div>

          <div className="min-h-106 bg-background w-full">
            <StripeEmbeddedCheckout clientSecret={clientSecret} />
          </div>

          <div className="mt-4 border-t p-4">
            <p className="text-muted-foreground text-center text-xs">
              Payments are securely processed by Stripe.
            </p>
          </div>
        </Card>
      </div>
    </div>
  );
}
